import { ApiError } from './api.js'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE CUSTOMER CONSOLE'S ONLY DIRECT CROSS-ORIGIN CALLER — `DR-033`.
 *
 * Every other fetch in this frontend goes to this console's OWN backend
 * (`api.ts`'s `/api/v1`), which proxies to Cadence server-side so the
 * browser never holds the shared `LEDGER_READER_TOKEN`. The customer
 * console's own session (`pruvato_tenant_session`) carries no such shared
 * secret — it's one tenant member's own credential — so `DR-033` ruled this
 * calls `customer-app` directly, same-site but cross-origin, rather than
 * building a second proxy layer to protect a secret that was never the kind
 * that pattern exists to protect.
 *
 * **`credentials: 'include'`, not `'same-origin'`** — the whole point of a
 * cross-origin, same-site cookie is that it travels on a request to a
 * sibling subdomain, which the default omits.
 *
 * No CSRF header. Checked every REQ answer this session produced for one:
 * none mentions a CSRF mechanism on `customer-app`'s tenant-auth/tenant-team
 * routes, unlike this console's own backend's double-submit cookie. Treated
 * as "not documented, not built," not silently assumed absent — flagged in
 * `LGH-040`'s own report.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **`localhost`, not `127.0.0.1` — `TST-001`'s own finding 1.** `localhost` and `127.0.0.1` are
 * different *sites* even though both resolve to the same box, so a `SameSite=Lax` session cookie set
 * for one is silently discarded by the browser on a response served from the other. `customer-app`'s
 * own `TENANT_CONSOLE_ORIGINS` allow-list and `APP_BASE_URL` both already assume `localhost` — this
 * default has to match that, not the other equally-valid-looking loopback address.
 */

const CUSTOMER_APP_BASE_URL = import.meta.env.VITE_CUSTOMER_APP_BASE_URL ?? 'http://localhost:3200'

interface Parser<T> {
  parse: (value: unknown) => T
}

async function errorMessageFrom(response: Response, path: string): Promise<string> {
  try {
    const body: unknown = await response.json()
    if (
      typeof body === 'object' &&
      body !== null &&
      'error' in body &&
      typeof body.error === 'object' &&
      body.error !== null &&
      'message' in body.error &&
      typeof body.error.message === 'string'
    ) {
      return body.error.message
    }
  } catch {
    // Not JSON, or not the expected shape — fall through to the generic message.
  }
  return `Request to ${path} failed.`
}

async function customerAppRequest(path: string, init: RequestInit): Promise<Response> {
  let response: Response
  try {
    // `Headers`, not an object spread — `init.headers` (`HeadersInit`) can be
    // an array of pairs, and spreading that into an object turns the pairs
    // into numeric keys and silently drops them. Same fix `http.ts` already
    // applies for the console's own backend calls.
    const headers = new Headers({ accept: 'application/json' })
    new Headers(init.headers).forEach((value, key) => {
      headers.set(key, value)
    })
    response = await fetch(`${CUSTOMER_APP_BASE_URL}${path}`, {
      credentials: 'include',
      ...init,
      headers,
    })
  } catch (cause) {
    throw new ApiError('Could not reach customer-app.', 0, { cause })
  }

  if (!response.ok) {
    throw new ApiError(await errorMessageFrom(response, path), response.status)
  }
  return response
}

export async function customerAppGet<T>(path: string, schema: Parser<T>): Promise<T> {
  const response = await customerAppRequest(path, { method: 'GET' })
  return schema.parse(await response.json())
}

export async function customerAppPost<T>(
  path: string,
  body: unknown,
  schema: Parser<T>,
): Promise<T> {
  const response = await customerAppRequest(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  return schema.parse(await response.json())
}

/**
 * Multipart, not JSON — the only caller today is the price-list upload,
 * which carries a real file. No `content-type` header set here deliberately:
 * the browser's own `fetch` sets it from the `FormData` boundary itself, and
 * overriding it manually drops the boundary parameter, breaking the parse.
 */
export async function customerAppPostForm<T>(
  path: string,
  form: FormData,
  schema: Parser<T>,
): Promise<T> {
  const response = await customerAppRequest(path, { method: 'POST', body: form })
  return schema.parse(await response.json())
}

export async function customerAppPut<T>(
  path: string,
  body: unknown,
  schema: Parser<T>,
): Promise<T> {
  const response = await customerAppRequest(path, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  return schema.parse(await response.json())
}

export async function customerAppDelete<T>(path: string, schema: Parser<T>): Promise<T> {
  const response = await customerAppRequest(path, { method: 'DELETE' })
  if (response.status === 204) return schema.parse(undefined)
  return schema.parse(await response.json())
}
